import { Tutorial } from '@/lib/tutorialTypes';
import {
  ChartBar,
  ChartLine,
  Database,
  FlowArrow,
  Sparkle,
  Target,
  Users,
} from '@phosphor-icons/react';

export const beginnerTutorials: Tutorial[] = [
  {
    id: 'getting-started',
    title: 'Your First Data Exploration',
    description:
      'Learn the basics of selecting datasets and understanding data quality in just 5 minutes.',
    difficulty: 'beginner',
    estimatedTime: '5 min',
    icon: <Database className='w-5 h-5' />,
    steps: [
      {
        id: 'welcome',
        title: 'Welcome to Data Science!',
        description:
          "Let's start your journey into data analysis with a simple exploration.",
        content: (
          <div className='space-y-4'>
            <p>
              Data science is like being a detective with numbers! You'll learn
              to:
            </p>
            <ul className='list-disc list-inside space-y-2 text-sm'>
              <li>
                <strong>Discover interesting datasets</strong> from real
                organizations
              </li>
              <li>
                <strong>Check data quality</strong> to see if the data is
                reliable
              </li>
              <li>
                <strong>Find patterns</strong> and connections between different
                datasets
              </li>
              <li>
                <strong>Create visualizations</strong> to tell stories with data
              </li>
            </ul>
            <div className='bg-blue-50 p-4 rounded-lg border border-blue-200'>
              <p className='text-sm text-blue-800'>
                💡 <strong>Pro tip:</strong> Think of datasets as puzzle pieces
                - each one contains part of a bigger picture!
              </p>
            </div>
          </div>
        ),
      },
      {
        id: 'templates',
        title: 'Choose Your Interest Area',
        description:
          'Research templates help you find datasets related to topics you care about.',
        content: (
          <div className='space-y-4'>
            <p>
              Research templates are like choosing your adventure! Each template
              focuses on a specific area:
            </p>
            <div className='grid grid-cols-1 gap-3'>
              <div className='bg-green-50 p-3 rounded border border-green-200'>
                <strong className='text-green-800'>
                  🌍 Climate & Environment:
                </strong>
                <span className='text-sm text-green-700'>
                  {' '}
                  Weather, pollution, and sustainability data
                </span>
              </div>
              <div className='bg-blue-50 p-3 rounded border border-blue-200'>
                <strong className='text-blue-800'>🏥 Public Health:</strong>
                <span className='text-sm text-blue-700'>
                  {' '}
                  Disease trends, health statistics, and medical research
                </span>
              </div>
              <div className='bg-purple-50 p-3 rounded border border-purple-200'>
                <strong className='text-purple-800'>
                  💰 Economics & Finance:
                </strong>
                <span className='text-sm text-purple-700'>
                  {' '}
                  Market trends, economic indicators, and financial data
                </span>
              </div>
            </div>
            <p className='text-sm text-muted-foreground'>
              Templates automatically filter data sources to show you the most
              relevant options for your interests.
            </p>
          </div>
        ),
        action: {
          text: 'Browse Templates',
          callback: () => {}, // Will be set by parent component
        },
      },
      {
        id: 'datasets',
        title: 'Select Your First Dataset',
        description:
          'Learn how to evaluate and choose quality datasets for analysis.',
        content: (
          <div className='space-y-4'>
            <p>When choosing datasets, look for these key indicators:</p>
            <div className='space-y-3'>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-bold'>
                  1
                </div>
                <div>
                  <strong>Quality Score:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Higher percentages mean cleaner, more reliable data
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-bold'>
                  2
                </div>
                <div>
                  <strong>Dataset Size:</strong>
                  <p className='text-sm text-muted-foreground'>
                    More rows usually mean more comprehensive data
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-bold'>
                  3
                </div>
                <div>
                  <strong>Data Types:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Numbers are great for analysis, text tells stories
                  </p>
                </div>
              </div>
            </div>
            <div className='bg-yellow-50 p-4 rounded-lg border border-yellow-200'>
              <p className='text-sm text-yellow-800'>
                🎯 <strong>Start simple:</strong> Choose datasets with 80%+
                quality scores for your first analysis!
              </p>
            </div>
          </div>
        ),
        action: {
          text: 'Explore Data Sources',
          callback: () => {}, // Will be set by parent component
        },
      },
      {
        id: 'quality',
        title: 'Understanding Data Quality',
        description:
          'Learn to read quality reports and understand what makes data reliable.',
        content: (
          <div className='space-y-4'>
            <p>
              Data quality reports are like a health check for your datasets:
            </p>
            <div className='space-y-3'>
              <div className='bg-green-50 p-3 rounded border border-green-200'>
                <strong className='text-green-800'>✅ Completeness:</strong>
                <span className='text-sm text-green-700'>
                  {' '}
                  How much data is missing? Less missing = better!
                </span>
              </div>
              <div className='bg-blue-50 p-3 rounded border border-blue-200'>
                <strong className='text-blue-800'>🎯 Consistency:</strong>
                <span className='text-sm text-blue-700'>
                  {' '}
                  Are formats and values standardized?
                </span>
              </div>
              <div className='bg-purple-50 p-3 rounded border border-purple-200'>
                <strong className='text-purple-800'>🔍 Accuracy:</strong>
                <span className='text-sm text-purple-700'>
                  {' '}
                  Do the values make sense and follow expected patterns?
                </span>
              </div>
            </div>
            <p className='text-sm text-muted-foreground'>
              Quality scores combine all these factors. Start with high-quality
              datasets to avoid common beginner pitfalls!
            </p>
          </div>
        ),
        action: {
          text: 'Check Quality Reports',
          callback: () => {}, // Will be set by parent component
        },
      },
      {
        id: 'success',
        title: "You're Ready to Explore!",
        description:
          'Congratulations! You now know the basics of data exploration.',
        content: (
          <div className='space-y-4'>
            <div className='text-center'>
              <div className='w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center'>
                <Target className='w-8 h-8 text-green-600' />
              </div>
              <h3 className='text-lg font-semibold text-green-800 mb-2'>
                Great job!
              </h3>
              <p className='text-green-700'>
                You've learned the fundamentals of data exploration. You're
                ready to:
              </p>
            </div>
            <div className='grid grid-cols-1 gap-3'>
              <div className='bg-white p-3 rounded border'>
                <strong>📊 Select multiple datasets</strong> to find interesting
                connections
              </div>
              <div className='bg-white p-3 rounded border'>
                <strong>🔍 Analyze correlations</strong> between different data
                sources
              </div>
              <div className='bg-white p-3 rounded border'>
                <strong>📈 Create visualizations</strong> to see patterns and
                trends
              </div>
            </div>
            <p className='text-sm text-center text-muted-foreground'>
              Continue with more advanced tutorials when you're ready!
            </p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'data-analysis',
    title: 'Finding Patterns in Data',
    description:
      'Discover how to analyze correlations and create meaningful insights from multiple datasets.',
    difficulty: 'beginner',
    estimatedTime: '8 min',
    icon: <ChartLine className='w-5 h-5' />,
    steps: [
      {
        id: 'correlations-intro',
        title: 'What Are Data Correlations?',
        description:
          'Learn how different datasets can be connected and reveal hidden patterns.',
        content: (
          <div className='space-y-4'>
            <p>
              Correlations help you discover relationships between different
              pieces of data:
            </p>
            <div className='space-y-3'>
              <div className='bg-blue-50 p-4 rounded-lg border border-blue-200'>
                <strong className='text-blue-800'>
                  Example: Weather & Sales
                </strong>
                <p className='text-sm text-blue-700 mt-1'>
                  Ice cream sales might increase when temperature rises - that's
                  a positive correlation!
                </p>
              </div>
              <div className='bg-green-50 p-4 rounded-lg border border-green-200'>
                <strong className='text-green-800'>
                  Example: Education & Employment
                </strong>
                <p className='text-sm text-green-700 mt-1'>
                  Higher education levels might correlate with lower
                  unemployment rates in certain regions.
                </p>
              </div>
            </div>
            <div className='bg-yellow-50 p-4 rounded-lg border border-yellow-200'>
              <p className='text-sm text-yellow-800'>
                ⚠️ <strong>Remember:</strong> Correlation doesn't mean
                causation! Just because two things happen together doesn't mean
                one causes the other.
              </p>
            </div>
          </div>
        ),
      },
      {
        id: 'select-multiple',
        title: 'Selecting Multiple Datasets',
        description:
          'Learn how to choose complementary datasets for correlation analysis.',
        content: (
          <div className='space-y-4'>
            <p>
              To find correlations, you need at least 2 datasets. Look for
              datasets that might be related:
            </p>
            <div className='space-y-3'>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  1
                </div>
                <div>
                  <strong>Same Time Period:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Data from the same years or months
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  2
                </div>
                <div>
                  <strong>Same Geographic Area:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Data from the same countries, states, or cities
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  3
                </div>
                <div>
                  <strong>Related Topics:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Areas that might influence each other
                  </p>
                </div>
              </div>
            </div>
            <p className='text-sm text-muted-foreground'>
              Try selecting 2-3 datasets from similar categories to start your
              analysis!
            </p>
          </div>
        ),
        action: {
          text: 'Add More Datasets',
          callback: () => {}, // Will be set by parent component
        },
      },
      {
        id: 'analyze',
        title: 'Running Correlation Analysis',
        description:
          'See how the system identifies potential relationships between your selected datasets.',
        content: (
          <div className='space-y-4'>
            <p>
              Once you have multiple datasets selected, you can analyze their
              correlations:
            </p>
            <div className='bg-muted/50 p-4 rounded-lg'>
              <h4 className='font-semibold mb-2'>
                What the analysis shows you:
              </h4>
              <ul className='list-disc list-inside space-y-1 text-sm'>
                <li>
                  <strong>Field matches:</strong> Which data columns can be
                  compared
                </li>
                <li>
                  <strong>Strength scores:</strong> How strong the relationship
                  appears to be
                </li>
                <li>
                  <strong>Data types:</strong> What kind of analysis is possible
                </li>
                <li>
                  <strong>Recommendations:</strong> Suggested next steps for
                  deeper analysis
                </li>
              </ul>
            </div>
            <div className='bg-green-50 p-4 rounded-lg border border-green-200'>
              <p className='text-sm text-green-800'>
                🔍 <strong>Look for:</strong> High correlation scores (0.7+) and
                large sample sizes for the most reliable insights!
              </p>
            </div>
          </div>
        ),
        action: {
          text: 'Analyze Correlations',
          callback: () => {}, // Will be set by parent component
        },
      },
      {
        id: 'interpret',
        title: 'Understanding Your Results',
        description:
          'Learn how to read correlation results and identify meaningful patterns.',
        content: (
          <div className='space-y-4'>
            <p>Here's how to interpret your correlation analysis results:</p>
            <div className='space-y-3'>
              <div className='bg-green-50 p-3 rounded border border-green-200'>
                <strong className='text-green-800'>
                  Strong Correlation (0.7-1.0):
                </strong>
                <p className='text-sm text-green-700'>
                  Strong relationship - worth investigating further!
                </p>
              </div>
              <div className='bg-yellow-50 p-3 rounded border border-yellow-200'>
                <strong className='text-yellow-800'>
                  Moderate Correlation (0.3-0.7):
                </strong>
                <p className='text-sm text-yellow-700'>
                  Some relationship - might be interesting with more data
                </p>
              </div>
              <div className='bg-red-50 p-3 rounded border border-red-200'>
                <strong className='text-red-800'>
                  Weak Correlation (0.0-0.3):
                </strong>
                <p className='text-sm text-red-700'>
                  Little to no relationship - try different datasets
                </p>
              </div>
            </div>
            <div className='bg-blue-50 p-4 rounded-lg border border-blue-200'>
              <p className='text-sm text-blue-800'>
                💡 <strong>Next steps:</strong> Strong correlations are great
                candidates for visualization and deeper analysis!
              </p>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'data-transformation',
    title: 'Data Transformation Pipelines',
    description:
      'Learn to clean, transform, and prepare data using visual pipeline builder',
    difficulty: 'intermediate',
    estimatedTime: '8-12 minutes',
    prerequisites: ['basic-analysis'],
    icon: '⚙️',
    steps: [
      {
        id: 'transform-intro',
        title: 'What is Data Transformation?',
        description:
          'Learn why data transformation is crucial for meaningful analysis.',
        content: (
          <div className='space-y-4'>
            <div className='text-center'>
              <div className='w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center'>
                <FlowArrow className='w-8 h-8 text-purple-600' />
              </div>
              <h3 className='text-lg font-semibold text-purple-800 mb-2'>
                Transform Raw Data into Gold!
              </h3>
            </div>
            <p>
              Raw data is like uncut gems - they need processing to reveal their
              true value. Data transformation helps you:
            </p>
            <div className='space-y-3'>
              <div className='bg-green-50 p-4 rounded-lg border border-green-200'>
                <strong className='text-green-800'>
                  🧹 Clean & Standardize
                </strong>
                <p className='text-sm text-green-700 mt-1'>
                  Remove duplicates, handle missing values, and fix inconsistent
                  formats
                </p>
              </div>
              <div className='bg-blue-50 p-4 rounded-lg border border-blue-200'>
                <strong className='text-blue-800'>🔄 Reshape & Combine</strong>
                <p className='text-sm text-blue-700 mt-1'>
                  Filter, group, aggregate, and join multiple datasets for
                  comprehensive analysis
                </p>
              </div>
              <div className='bg-orange-50 p-4 rounded-lg border border-orange-200'>
                <strong className='text-orange-800'>
                  📊 Enrich & Calculate
                </strong>
                <p className='text-sm text-orange-700 mt-1'>
                  Create new calculated fields and derive insights from existing
                  data
                </p>
              </div>
            </div>
            <div className='bg-yellow-50 p-4 rounded-lg border border-yellow-200'>
              <p className='text-sm text-yellow-800'>
                💡 <strong>Remember:</strong> Well-transformed data leads to
                more accurate insights and better decisions!
              </p>
            </div>
          </div>
        ),
      },
      {
        id: 'pipeline-builder',
        title: 'Visual Pipeline Builder',
        description:
          'Learn how to create transformation workflows using our drag-and-drop interface.',
        content: (
          <div className='space-y-4'>
            <p>
              Our pipeline builder makes data transformation as easy as building
              with blocks:
            </p>
            <div className='space-y-3'>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  1
                </div>
                <div>
                  <strong>Choose Transformation Steps:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Select from filtering, sorting, grouping, and more
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  2
                </div>
                <div>
                  <strong>Configure Each Step:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Set parameters like columns to process and conditions
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  3
                </div>
                <div>
                  <strong>Preview Results:</strong>
                  <p className='text-sm text-muted-foreground'>
                    See sample outputs before running the full pipeline
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  4
                </div>
                <div>
                  <strong>Execute & Save:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Run your pipeline and save it for future use
                  </p>
                </div>
              </div>
            </div>
          </div>
        ),
        action: {
          text: 'Open Pipeline Builder',
          callback: () => {}, // Will be set by parent component
        },
      },
      {
        id: 'transformation-types',
        title: 'Common Transformation Operations',
        description:
          'Explore the different types of transformations you can apply to your data.',
        content: (
          <div className='space-y-4'>
            <p>Here are the most common data transformation operations:</p>
            <div className='grid grid-cols-1 gap-3'>
              <div className='bg-blue-50 p-3 rounded border border-blue-200'>
                <strong className='text-blue-800'>🔍 Filter Rows</strong>
                <p className='text-sm text-blue-700'>
                  Keep only data that meets specific conditions (e.g., dates
                  after 2020)
                </p>
              </div>
              <div className='bg-green-50 p-3 rounded border border-green-200'>
                <strong className='text-green-800'>📊 Sort Data</strong>
                <p className='text-sm text-green-700'>
                  Arrange rows by column values (alphabetical, numerical,
                  chronological)
                </p>
              </div>
              <div className='bg-purple-50 p-3 rounded border border-purple-200'>
                <strong className='text-purple-800'>
                  📋 Group & Aggregate
                </strong>
                <p className='text-sm text-purple-700'>
                  Summarize data by categories (sum sales by region, average by
                  month)
                </p>
              </div>
              <div className='bg-orange-50 p-3 rounded border border-orange-200'>
                <strong className='text-orange-800'>🔗 Join Datasets</strong>
                <p className='text-sm text-orange-700'>
                  Combine related data from multiple sources
                </p>
              </div>
              <div className='bg-red-50 p-3 rounded border border-red-200'>
                <strong className='text-red-800'>🧹 Clean Data</strong>
                <p className='text-sm text-red-700'>
                  Handle missing values, remove duplicates, fix formatting
                  issues
                </p>
              </div>
              <div className='bg-teal-50 p-3 rounded border border-teal-200'>
                <strong className='text-teal-800'>⚡ Transform Values</strong>
                <p className='text-sm text-teal-700'>
                  Calculate new fields, convert units, apply formulas
                </p>
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'pipeline-templates',
        title: 'Using Pipeline Templates',
        description:
          'Save time with pre-built transformation workflows for common scenarios.',
        content: (
          <div className='space-y-4'>
            <p>
              Start faster with our library of pre-built pipeline templates:
            </p>
            <div className='space-y-3'>
              <div className='bg-green-50 p-4 rounded-lg border border-green-200'>
                <strong className='text-green-800'>
                  🧹 Basic Data Cleaning
                </strong>
                <p className='text-sm text-green-700 mt-1'>
                  Remove duplicates, handle missing values, standardize formats
                </p>
              </div>
              <div className='bg-blue-50 p-4 rounded-lg border border-blue-200'>
                <strong className='text-blue-800'>📈 Sales Analysis</strong>
                <p className='text-sm text-blue-700 mt-1'>
                  Time-series aggregation, revenue calculations, trend analysis
                </p>
              </div>
              <div className='bg-purple-50 p-4 rounded-lg border border-purple-200'>
                <strong className='text-purple-800'>
                  👥 Customer Segmentation
                </strong>
                <p className='text-sm text-purple-700 mt-1'>
                  Behavioral analysis, value-based grouping, profile creation
                </p>
              </div>
              <div className='bg-orange-50 p-4 rounded-lg border border-orange-200'>
                <strong className='text-orange-800'>
                  🔗 Multi-Dataset Join
                </strong>
                <p className='text-sm text-orange-700 mt-1'>
                  Combine related datasets for comprehensive analysis
                </p>
              </div>
            </div>
            <div className='bg-yellow-50 p-4 rounded-lg border border-yellow-200'>
              <p className='text-sm text-yellow-800'>
                💡 <strong>Pro tip:</strong> Templates are fully customizable -
                use them as starting points for your own workflows!
              </p>
            </div>
          </div>
        ),
        action: {
          text: 'Browse Templates',
          callback: () => {}, // Will be set by parent component
        },
      },
      {
        id: 'pipeline-best-practices',
        title: 'Pipeline Best Practices',
        description:
          'Learn tips for creating efficient and maintainable data transformation workflows.',
        content: (
          <div className='space-y-4'>
            <p>
              Follow these best practices for successful data transformation:
            </p>
            <div className='space-y-3'>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-bold'>
                  ✓
                </div>
                <div>
                  <strong>Start Simple:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Begin with basic operations and add complexity gradually
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-bold'>
                  ✓
                </div>
                <div>
                  <strong>Preview Early & Often:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Check sample outputs after each step to catch issues quickly
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-bold'>
                  ✓
                </div>
                <div>
                  <strong>Document Your Steps:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Use clear names and descriptions for future reference
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-bold'>
                  ✓
                </div>
                <div>
                  <strong>Save Successful Pipelines:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Reuse proven workflows for similar datasets
                  </p>
                </div>
              </div>
            </div>
            <div className='bg-red-50 p-4 rounded-lg border border-red-200'>
              <p className='text-sm text-red-800'>
                ⚠️ <strong>Remember:</strong> Always backup original data before
                running transformations!
              </p>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'visualization',
    title: 'Creating Your First Charts',
    description:
      'Learn to create meaningful visualizations that tell stories with your data.',
    difficulty: 'beginner',
    estimatedTime: '6 min',
    icon: <ChartBar className='w-5 h-5' />,
    steps: [
      {
        id: 'viz-intro',
        title: 'Why Visualize Data?',
        description:
          'Understand the power of turning numbers into visual stories.',
        content: (
          <div className='space-y-4'>
            <p>
              Data visualization turns boring numbers into compelling stories
              that anyone can understand:
            </p>
            <div className='grid grid-cols-1 gap-3'>
              <div className='bg-purple-50 p-4 rounded-lg border border-purple-200'>
                <strong className='text-purple-800'>
                  📊 Spot Trends Instantly
                </strong>
                <p className='text-sm text-purple-700 mt-1'>
                  See patterns that would take hours to find in spreadsheets
                </p>
              </div>
              <div className='bg-blue-50 p-4 rounded-lg border border-blue-200'>
                <strong className='text-blue-800'>
                  🎯 Communicate Insights
                </strong>
                <p className='text-sm text-blue-700 mt-1'>
                  Share your discoveries with others in an easy-to-understand
                  way
                </p>
              </div>
              <div className='bg-green-50 p-4 rounded-lg border border-green-200'>
                <strong className='text-green-800'>🔍 Identify Outliers</strong>
                <p className='text-sm text-green-700 mt-1'>
                  Quickly spot unusual data points that need investigation
                </p>
              </div>
            </div>
            <p className='text-sm text-muted-foreground'>
              A good chart can tell a story that a thousand numbers cannot!
            </p>
          </div>
        ),
      },
      {
        id: 'chart-types',
        title: 'Choosing the Right Chart Type',
        description:
          'Learn which charts work best for different types of data and questions.',
        content: (
          <div className='space-y-4'>
            <p>
              Different chart types answer different questions about your data:
            </p>
            <div className='space-y-3'>
              <div className='bg-white p-3 rounded border'>
                <strong>📈 Line Charts:</strong>
                <span className='text-sm text-muted-foreground'>
                  {' '}
                  Show trends over time (like stock prices or temperature
                  changes)
                </span>
              </div>
              <div className='bg-white p-3 rounded border'>
                <strong>📊 Bar Charts:</strong>
                <span className='text-sm text-muted-foreground'>
                  {' '}
                  Compare different categories (like sales by region)
                </span>
              </div>
              <div className='bg-white p-3 rounded border'>
                <strong>🥧 Pie Charts:</strong>
                <span className='text-sm text-muted-foreground'>
                  {' '}
                  Show parts of a whole (like budget breakdown)
                </span>
              </div>
              <div className='bg-white p-3 rounded border'>
                <strong>📍 Scatter Plots:</strong>
                <span className='text-sm text-muted-foreground'>
                  {' '}
                  Reveal relationships between two variables
                </span>
              </div>
            </div>
            <div className='bg-yellow-50 p-4 rounded-lg border border-yellow-200'>
              <p className='text-sm text-yellow-800'>
                🎯 <strong>Pro tip:</strong> Start with simple bar or line
                charts - they're easy to read and understand!
              </p>
            </div>
          </div>
        ),
        action: {
          text: 'Create Visualization',
          callback: () => {}, // Will be set by parent component
        },
      },
      {
        id: 'customize',
        title: 'Making Charts Clear and Beautiful',
        description:
          'Learn design principles that make your visualizations effective.',
        content: (
          <div className='space-y-4'>
            <p>
              Great visualizations are both accurate and beautiful. Focus on:
            </p>
            <div className='space-y-3'>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-bold'>
                  1
                </div>
                <div>
                  <strong>Clear Titles:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Tell people exactly what they're looking at
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-bold'>
                  2
                </div>
                <div>
                  <strong>Labeled Axes:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Include units (dollars, percentages, etc.)
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-bold'>
                  3
                </div>
                <div>
                  <strong>Appropriate Colors:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Use colors that make sense (red for danger, green for
                    growth)
                  </p>
                </div>
              </div>
            </div>
            <div className='bg-blue-50 p-4 rounded-lg border border-blue-200'>
              <p className='text-sm text-blue-800'>
                🎨 <strong>Remember:</strong> If your grandmother can't
                understand your chart, it needs to be simpler!
              </p>
            </div>
          </div>
        ),
      },
      {
        id: 'share',
        title: 'Sharing Your Insights',
        description:
          'Learn how to present your findings and tell compelling data stories.',
        content: (
          <div className='space-y-4'>
            <div className='text-center'>
              <div className='w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center'>
                <Users className='w-8 h-8 text-blue-600' />
              </div>
              <h3 className='text-lg font-semibold text-blue-800 mb-2'>
                You're a Data Storyteller!
              </h3>
            </div>
            <p>Now you can turn raw data into insights that matter:</p>
            <div className='space-y-3'>
              <div className='bg-white p-3 rounded border'>
                <strong>📱 Export your charts</strong> to include in
                presentations or reports
              </div>
              <div className='bg-white p-3 rounded border'>
                <strong>📝 Write clear explanations</strong> of what the data
                shows
              </div>
              <div className='bg-white p-3 rounded border'>
                <strong>🤔 Ask follow-up questions</strong> to guide future
                analysis
              </div>
            </div>
            <div className='bg-green-50 p-4 rounded-lg border border-green-200'>
              <p className='text-sm text-green-800'>
                🎉 <strong>Congratulations!</strong> You've completed the
                visualization basics. Keep practicing with different datasets!
              </p>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    id: 'templates-guide',
    title: 'Using Research Templates',
    description:
      'Master research templates to quickly find relevant datasets for specific domains.',
    difficulty: 'beginner',
    estimatedTime: '4 min',
    icon: <Sparkle className='w-5 h-5' />,
    steps: [
      {
        id: 'template-benefits',
        title: 'Why Use Templates?',
        description:
          'Understand how templates save time and guide your research focus.',
        content: (
          <div className='space-y-4'>
            <p>
              Research templates are like having an expert guide for different
              fields of study:
            </p>
            <div className='space-y-3'>
              <div className='bg-blue-50 p-4 rounded-lg border border-blue-200'>
                <strong className='text-blue-800'>🎯 Focused Discovery</strong>
                <p className='text-sm text-blue-700 mt-1'>
                  Skip the overwhelming choice - see only datasets relevant to
                  your interest area
                </p>
              </div>
              <div className='bg-green-50 p-4 rounded-lg border border-green-200'>
                <strong className='text-green-800'>📚 Expert Curation</strong>
                <p className='text-sm text-green-700 mt-1'>
                  Get pre-selected, high-quality datasets commonly used in each
                  research domain
                </p>
              </div>
              <div className='bg-purple-50 p-4 rounded-lg border border-purple-200'>
                <strong className='text-purple-800'>🚀 Quick Start</strong>
                <p className='text-sm text-purple-700 mt-1'>
                  Begin analyzing immediately with relevant data combinations
                </p>
              </div>
            </div>
            <p className='text-sm text-muted-foreground'>
              Think of templates as specialized research labs - each one
              designed for specific types of discovery!
            </p>
          </div>
        ),
        action: {
          text: 'Explore Templates',
          callback: () => {}, // Will be set by parent component
        },
      },
      {
        id: 'template-types',
        title: 'Available Research Domains',
        description:
          'Learn about different template categories and what they offer.',
        content: (
          <div className='space-y-4'>
            <p>
              Each template focuses on a specific research area with curated
              datasets:
            </p>
            <div className='grid grid-cols-1 gap-3'>
              <div className='bg-green-50 p-3 rounded border border-green-200'>
                <strong className='text-green-800'>
                  🌍 Climate & Environment
                </strong>
                <p className='text-sm text-green-700'>
                  Weather patterns, pollution data, renewable energy,
                  biodiversity
                </p>
              </div>
              <div className='bg-red-50 p-3 rounded border border-red-200'>
                <strong className='text-red-800'>🏥 Public Health</strong>
                <p className='text-sm text-red-700'>
                  Disease tracking, health outcomes, medical research,
                  demographics
                </p>
              </div>
              <div className='bg-blue-50 p-3 rounded border border-blue-200'>
                <strong className='text-blue-800'>
                  💰 Economics & Finance
                </strong>
                <p className='text-sm text-blue-700'>
                  Market data, economic indicators, trade statistics, employment
                </p>
              </div>
              <div className='bg-purple-50 p-3 rounded border border-purple-200'>
                <strong className='text-purple-800'>🏛️ Social Sciences</strong>
                <p className='text-sm text-purple-700'>
                  Population studies, education data, social trends, policy
                  research
                </p>
              </div>
            </div>
            <p className='text-sm text-muted-foreground'>
              More templates are added regularly based on popular research areas
              and user requests!
            </p>
          </div>
        ),
      },
      {
        id: 'template-workflow',
        title: 'Template Workflow',
        description:
          'Learn the step-by-step process of using templates effectively.',
        content: (
          <div className='space-y-4'>
            <p>Here's how to make the most of research templates:</p>
            <div className='space-y-3'>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  1
                </div>
                <div>
                  <strong>Choose Your Domain:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Pick the template that matches your research interest
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  2
                </div>
                <div>
                  <strong>Review Filtered Results:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Explore the curated data sources in the Discover tab
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  3
                </div>
                <div>
                  <strong>Select Datasets:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Add relevant datasets to your workspace
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold'>
                  4
                </div>
                <div>
                  <strong>Clear When Done:</strong>
                  <p className='text-sm text-muted-foreground'>
                    Remove the template filter to explore other domains
                  </p>
                </div>
              </div>
            </div>
            <div className='bg-yellow-50 p-4 rounded-lg border border-yellow-200'>
              <p className='text-sm text-yellow-800'>
                💡 <strong>Tip:</strong> You can apply multiple templates in
                sequence to explore different aspects of a topic!
              </p>
            </div>
          </div>
        ),
        action: {
          text: 'Try a Template',
          callback: () => {}, // Will be set by parent component
        },
      },
    ],
  },
];
